import React, { createContext, ReactNode, Reducer, useContext, useReducer } from 'react';

interface Player {
  person?: {
    personID: string;
    nativeGivenName: string;
    nativeFamilyName: string;
  };
}

interface RoundsPositions {
  isDualMatch: boolean;
  roundName: string;
  collectionPosition: string | number;
}

interface Team {
  name: string;
  id: string;
}

interface ResultData {
  date?: string;
  thisPlayers: Player[];
  opponentPlayers: Player[];
  scores?: [];
  team: Partial<Team>;
  thisPlayerWon: boolean;
  roundsAndPositions: Partial<RoundsPositions>;
}

interface State {
  isOpen: boolean;
  resultData: ResultData;
}

type Dispatch = (action: Action) => void;

enum ActionTypes {
  OPEN_MODAL,
  CLOSE_MODAL,
  SET_RESULT_DATA
}

type Action = { type: ActionTypes; payload?: ResultData };

interface IModalContext {
  modalState: State;
  dispatch: Dispatch;
}

const actions = {
  openModal: () => ({ type: ActionTypes.OPEN_MODAL }),
  closeModal: () => ({ type: ActionTypes.CLOSE_MODAL }),
  setResult: (data: ResultData) => ({
    type: ActionTypes.SET_RESULT_DATA,
    payload: data
  })
};

const ModalContext = createContext<IModalContext | undefined>(undefined);

const initialState: State = {
  isOpen: false,
  resultData: {
    date: '',
    thisPlayers: [],
    opponentPlayers: [],
    scores: [],
    team: {},
    thisPlayerWon: false,
    roundsAndPositions: {}
  }
};

const modalReducer: Reducer<State, Action> = (state, action) => {
  switch (action.type) {
    case ActionTypes.OPEN_MODAL:
      return { ...state, isOpen: true };
    case ActionTypes.CLOSE_MODAL:
      return { ...state, isOpen: false };
    case ActionTypes.SET_RESULT_DATA:
      return { ...state, resultData: action.payload as ResultData };
    default:
      return state;
  }
};

const ModalProvider = ({ children }: { children: ReactNode }) => {
  const [modalState, dispatch] = useReducer(modalReducer, initialState);

  const value: IModalContext = { modalState, dispatch };
  return <ModalContext.Provider value={value}>{children}</ModalContext.Provider>;
};

const useModal = () => {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error('useModal must be used within a ModalStateProvider');
  }
  return context;
};

export { ModalProvider, useModal, actions };
